import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Target, 
  Cpu, 
  HardDrive, 
  Database, 
  MemoryStick,
  TrendingUp,
  Clock,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Zap,
  Shield,
  Users,
  Timer
} from 'lucide-react';

interface MetricThreshold {
  name: string;
  target: string;
  current?: string;
  status: 'success' | 'warning' | 'danger' | 'pending';
  description: string;
}

interface SuccessPhase {
  phase: string;
  duration: string;
  status: 'completed' | 'in-progress' | 'pending';
  criteria: string[];
}

export function SuccessCriteria() {
  const [selectedCategory, setSelectedCategory] = useState<string>('overview');

  const cpuMetrics: MetricThreshold[] = [
    {
      name: 'Peak CPU Utilization',
      target: '<75% normal, <85% peak',
      current: '68%',
      status: 'success',
      description: 'CPU usage during normal and peak operations'
    },
    {
      name: 'CPU Wait Time',
      target: '<5% of execution time',
      current: '3.2%',
      status: 'success',
      description: 'Time spent waiting for CPU resources'
    },
    {
      name: 'Parallelism Efficiency',
      target: '>80% effectiveness',
      current: '85%',
      status: 'success',
      description: 'Effectiveness of parallel query execution'
    },
    {
      name: 'Context Switching',
      target: '<10K switches/sec/core',
      current: '8.5K',
      status: 'success',
      description: 'Context switches per second per core'
    }
  ];

  const memoryMetrics: MetricThreshold[] = [
    {
      name: 'Buffer Cache Hit Ratio',
      target: '>95% consistently',
      current: '97.2%',
      status: 'success',
      description: 'Percentage of data pages found in buffer cache'
    },
    {
      name: 'Page Life Expectancy',
      target: '>300s (ideally >600s)',
      current: '645s',
      status: 'success',
      description: 'Average time pages stay in buffer pool'
    },
    {
      name: 'Memory Grant Waits',
      target: '<5% of queries waiting',
      current: '2.1%',
      status: 'success',
      description: 'Queries waiting for memory grants'
    },
    {
      name: 'Memory Pressure Events',
      target: 'Zero events',
      current: '0',
      status: 'success',
      description: 'Memory pressure incidents during testing'
    }
  ];

  const ioMetrics: MetricThreshold[] = [
    {
      name: 'Disk Read Latency',
      target: '<15ms avg, <50ms max',
      current: '12ms avg',
      status: 'success',
      description: 'Average and maximum disk read response times'
    },
    {
      name: 'Disk Write Latency',
      target: '<10ms avg, <30ms max',
      current: '8ms avg',
      status: 'success',
      description: 'Average and maximum disk write response times'
    },
    {
      name: 'I/O Throughput',
      target: '>1000 IOPS per procedure',
      current: '1,250 IOPS',
      status: 'success',
      description: 'Input/output operations per second capability'
    },
    {
      name: 'I/O Wait Time',
      target: '<20% of execution time',
      current: '15%',
      status: 'success',
      description: 'Time spent waiting for I/O operations'
    }
  ];

  const tempdbMetrics: MetricThreshold[] = [
    {
      name: 'TempDB Space Usage',
      target: '<80% during peak load',
      current: '65%',
      status: 'success',
      description: 'Percentage of allocated TempDB space used'
    },
    {
      name: 'Allocation Contention',
      target: '<5% sessions waiting',
      current: '2.8%',
      status: 'success',
      description: 'Sessions waiting on TempDB allocation pages'
    },
    {
      name: 'Auto-Growth Events',
      target: 'Zero during testing',
      current: '0',
      status: 'success',
      description: 'TempDB file auto-growth occurrences'
    },
    {
      name: 'Version Store Usage',
      target: '<20% of TempDB space',
      current: '12%',
      status: 'success',
      description: 'Space used by version store in TempDB'
    }
  ];

  const businessMetrics = [
    {
      name: 'Revenue Protection',
      target: 'Zero revenue loss',
      impact: '$2.3M+ per hour saved',
      status: 'success' as const
    },
    {
      name: 'Customer Experience',
      target: '<3 second checkout',
      impact: '40% CSAT improvement',
      status: 'success' as const
    },
    {
      name: 'System Availability',
      target: '99.99% uptime',
      impact: 'Competitive advantage',
      status: 'success' as const
    },
    {
      name: 'Support Tickets',
      target: '60% reduction',
      impact: 'Operational efficiency',
      status: 'in-progress' as const
    }
  ];

  const testingPhases: SuccessPhase[] = [
    {
      phase: 'Baseline Establishment',
      duration: 'Week 1-2',
      status: 'completed',
      criteria: [
        'Complete performance baseline for all 10 stored procedures',
        'Identify current bottlenecks and resource constraints',
        'Establish monitoring and alerting thresholds',
        'Document existing performance characteristics'
      ]
    },
    {
      phase: 'Load Testing',
      duration: 'Week 3-4',
      status: 'in-progress',
      criteria: [
        'Successfully execute 100+ concurrent users per procedure',
        'Maintain performance thresholds under 2x normal load',
        'Zero system crashes or data corruption events',
        'Complete resource utilization analysis'
      ]
    },
    {
      phase: 'Stress Testing',
      duration: 'Week 5-6',
      status: 'pending',
      criteria: [
        'Identify breaking points for each procedure',
        'Validate automated recovery mechanisms',
        'Document performance degradation patterns',
        'Confirm system stability after stress events'
      ]
    },
    {
      phase: 'Optimization',
      duration: 'Week 7-8',
      status: 'pending',
      criteria: [
        'Implement performance improvements based on findings',
        'Achieve 20% improvement in response times',
        'Reduce resource consumption by 15%',
        'Validate improvements under stress conditions'
      ]
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'warning':
      case 'in-progress':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'danger':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return <Timer className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'danger':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const MetricCard = ({ metrics, icon, title, color }: { 
    metrics: MetricThreshold[], 
    icon: React.ReactNode, 
    title: string,
    color: string 
  }) => (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className={`p-2 ${color} text-white rounded-lg`}>
            {icon}
          </div>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {metrics.map((metric, index) => (
          <div key={index} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm">{metric.name}</span>
              <div className="flex items-center gap-2">
                {getStatusIcon(metric.status)}
                <Badge className={getStatusColor(metric.status)}>
                  {metric.current || 'Pending'}
                </Badge>
              </div>
            </div>
            <div className="text-xs text-gray-600">
              <div>Target: {metric.target}</div>
              <div>{metric.description}</div>
            </div>
            <Progress 
              value={metric.status === 'success' ? 100 : metric.status === 'warning' ? 70 : 30} 
              className="h-2"
            />
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            Costco Stress Testing Success Criteria
          </CardTitle>
          <p className="text-sm text-gray-600">
            Comprehensive success metrics and performance thresholds for the HighJump stored procedure stress testing initiative
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
            <TabsList className="grid w-full grid-cols-7">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cpu">CPU</TabsTrigger>
              <TabsTrigger value="memory">Memory</TabsTrigger>
              <TabsTrigger value="io">I/O</TabsTrigger>
              <TabsTrigger value="tempdb">TempDB</TabsTrigger>
              <TabsTrigger value="business">Business</TabsTrigger>
              <TabsTrigger value="phases">Phases</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              {/* Executive Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-shiftleft-50 border-shiftleft-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Shield className="w-8 h-8 text-shiftleft-600" />
                      <div>
                        <div className="text-2xl font-bold text-shiftleft-900">99.99%</div>
                        <div className="text-sm text-shiftleft-700">Uptime Target</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-8 h-8 text-green-600" />
                      <div>
                        <div className="text-2xl font-bold text-green-900">15:1</div>
                        <div className="text-sm text-green-700">ROI Target</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Zap className="w-8 h-8 text-purple-600" />
                      <div>
                        <div className="text-2xl font-bold text-purple-900">&lt;3s</div>
                        <div className="text-sm text-purple-700">Response Time</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="w-8 h-8 text-orange-600" />
                      <div>
                        <div className="text-2xl font-bold text-orange-900">500+</div>
                        <div className="text-sm text-orange-700">Concurrent Users</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Key Success Areas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Performance Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">CPU Utilization</span>
                      <Badge className="bg-green-100 text-green-800">68% ✓</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Memory Efficiency</span>
                      <Badge className="bg-green-100 text-green-800">97.2% ✓</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">I/O Response</span>
                      <Badge className="bg-green-100 text-green-800">12ms ✓</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">TempDB Usage</span>
                      <Badge className="bg-green-100 text-green-800">65% ✓</Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Business Impact</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Revenue Protection</span>
                      <Badge className="bg-green-100 text-green-800">$2.3M+ ✓</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Customer Experience</span>
                      <Badge className="bg-green-100 text-green-800">40% ↑</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Support Tickets</span>
                      <Badge className="bg-yellow-100 text-yellow-800">60% ↓ 🔄</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Azure Cost Savings</span>
                      <Badge className="bg-shiftleft-100 text-shiftleft-800">30% ↓</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="cpu" className="space-y-4">
              <MetricCard 
                metrics={cpuMetrics} 
                icon={<Cpu className="w-5 h-5" />}
                title="CPU Performance Metrics"
                color="bg-shiftleft-500"
              />
            </TabsContent>

            <TabsContent value="memory" className="space-y-4">
              <MetricCard 
                metrics={memoryMetrics} 
                icon={<MemoryStick className="w-5 h-5" />}
                title="Memory Performance Metrics"
                color="bg-green-500"
              />
            </TabsContent>

            <TabsContent value="io" className="space-y-4">
              <MetricCard 
                metrics={ioMetrics} 
                icon={<HardDrive className="w-5 h-5" />}
                title="I/O Performance Metrics"
                color="bg-purple-500"
              />
            </TabsContent>

            <TabsContent value="tempdb" className="space-y-4">
              <MetricCard 
                metrics={tempdbMetrics} 
                icon={<Database className="w-5 h-5" />}
                title="TempDB Performance Metrics"
                color="bg-orange-500"
              />
            </TabsContent>

            <TabsContent value="business" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Business Success Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {businessMetrics.map((metric, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium">{metric.name}</div>
                        <div className="text-sm text-gray-600">Target: {metric.target}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-green-600">{metric.impact}</div>
                        <Badge className={getStatusColor(metric.status)}>
                          {metric.status === 'success' ? 'Achieved' : 'In Progress'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="phases" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-shiftleft-600" />
                    Testing Phases & Success Gates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {testingPhases.map((phase, index) => (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(phase.status)}
                          <div>
                            <div className="font-medium">{phase.phase}</div>
                            <div className="text-sm text-gray-600">{phase.duration}</div>
                          </div>
                        </div>
                        <Badge className={getStatusColor(phase.status)}>
                          {phase.status.replace('-', ' ')}
                        </Badge>
                      </div>
                      <div className="ml-7 space-y-2">
                        {phase.criteria.map((criterion, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <div className="w-2 h-2 bg-shiftleft-500 rounded-full mt-2 flex-shrink-0"></div>
                            <span className={phase.status === 'completed' ? 'text-green-700' : 'text-gray-600'}>
                              {criterion}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}